/**
 * BenefitShareCard — one-tap share widget for a Volunteer Impact
 * article. Sits at the bottom of every /volunteer/benefits/:slug page.
 *
 * Supported channels:
 *   • Native Share API (mobile) — falls back to platform buttons on desktop.
 *   • Twitter/X · LinkedIn · Facebook · WhatsApp · Email
 *   • "Copy link" and "Copy caption" (writes a ready-to-post caption
 *     to the clipboard including hashtags).
 *
 * The visible preview mimics a shareable social card: hero image,
 * pull-quote and Social Pulse footer chip.
 */
import { useState } from "react";
import {
  Share2, Copy, Check, Twitter, Linkedin, Facebook,
  MessageCircle, Mail, ExternalLink,
} from "lucide-react";
import { http } from "@/lib/api";

const BASE_URL = (typeof window !== "undefined") ? window.location.origin : "";

function makeCaption(article) {
  return `Why I ${article.title.toLowerCase()} 💚\n\n${article.tagline}\n\n${article.headline}\n\nRead more on Social Pulse:\n#Volunteer #SocialCapital #${article.title.replace(/\s+/g, "")}`;
}

export default function BenefitShareCard({ article }) {
  const [copied, setCopied] = useState(null);
  const url     = `${BASE_URL}/volunteer/benefits/${article.slug}`;
  const caption = makeCaption(article);
  const shareTitle = `Why I ${article.title} — Social Pulse`;
  const shareText  = `${article.tagline}\n\nRead the full article on Social Pulse:`;

  const logShare = (channel) => {
    // fire-and-forget — never blocks or breaks share intent
    http.post("/volunteer/share-events", {
      benefit_slug:  article.slug,
      benefit_title: article.title,
      channel,
    }).catch(() => {});
  };

  const flash = (key) => {
    setCopied(key);
    setTimeout(() => setCopied(null), 1800);
  };

  const nativeShare = async () => {
    logShare("native");
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({ title: shareTitle, text: shareText, url });
      } catch { /* user cancelled */ }
    } else {
      // desktop: fall back to copy link
      copy(url, "link");
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      flash(key);
      logShare(key === "link" ? "copy_link" : "copy_caption");
    } catch { /* silent */ }
  };

  const twitterHref = `https://twitter.com/intent/tweet?text=${encodeURIComponent(caption)}&url=${encodeURIComponent(url)}`;
  const linkedInHref = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
  const facebookHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(article.headline)}`;
  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(`${caption}\n${url}`)}`;
  const emailHref    = `mailto:?subject=${encodeURIComponent(shareTitle)}&body=${encodeURIComponent(`${caption}\n\n${url}`)}`;

  const btn = "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-mono uppercase tracking-wider transition-all hover:-translate-y-0.5 hover:shadow-sm";

  return (
    <section
      className="mt-10 rounded-3xl border p-6 sm:p-8 overflow-hidden"
      style={{
        borderColor: "var(--wofy-border)",
        background: `linear-gradient(135deg, ${article.color}0f 0%, var(--wofy-surface) 60%)`,
      }}
      data-testid="volunteer-benefit-share"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1" style={{ color: article.color }}>
            SHARE THIS ARTICLE
          </div>
          <div className="font-display text-2xl leading-snug">
            Tell the world <span style={{ color: article.color }}>why you {article.title.toLowerCase()}</span>.
          </div>
          <p className="text-sm opacity-70 mt-1 max-w-lg">
            One-tap share this article — or copy a ready-made caption for
            Instagram, LinkedIn, or your favourite channel.
          </p>
        </div>

        <button
          data-testid="benefit-share-native"
          onClick={nativeShare}
          className="wofy-btn-primary text-sm"
          style={{ background: article.color, borderColor: article.color }}
        >
          <Share2 className="w-4 h-4" /> Share
        </button>
      </div>

      {/* Preview card (social-style) */}
      <div
        className="mt-6 rounded-2xl overflow-hidden border grid sm:grid-cols-[180px,1fr]"
        style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg)" }}
        data-testid="benefit-share-preview"
      >
        <div className="relative min-h-[130px]">
          <img src={article.hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, ${article.color}66, ${article.color}dd)` }} />
          <div className="absolute inset-0 flex items-center justify-center text-4xl">{article.emoji}</div>
        </div>
        <div className="p-4 sm:p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest opacity-70">Social Pulse · Volunteer Impact</div>
          <div className="font-display text-lg mt-1 leading-snug">{article.headline}</div>
          <div className="text-sm opacity-80 mt-1">{article.tagline}</div>
          <div className="text-xs opacity-60 mt-3 truncate">{url}</div>
        </div>
      </div>

      {/* Channel buttons */}
      <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="benefit-share-channels">
        <a href={twitterHref}  onClick={() => logShare("twitter")} target="_blank" rel="noopener noreferrer" className={btn} data-testid="benefit-share-twitter"  style={{ borderColor: "var(--wofy-border)" }}>
          <Twitter className="w-3.5 h-3.5" /> X / Twitter
        </a>
        <a href={linkedInHref} onClick={() => logShare("linkedin")} target="_blank" rel="noopener noreferrer" className={btn} data-testid="benefit-share-linkedin" style={{ borderColor: "var(--wofy-border)" }}>
          <Linkedin className="w-3.5 h-3.5" /> LinkedIn
        </a>
        <a href={facebookHref} onClick={() => logShare("facebook")} target="_blank" rel="noopener noreferrer" className={btn} data-testid="benefit-share-facebook" style={{ borderColor: "var(--wofy-border)" }}>
          <Facebook className="w-3.5 h-3.5" /> Facebook
        </a>
        <a href={whatsappHref} onClick={() => logShare("whatsapp")} target="_blank" rel="noopener noreferrer" className={btn} data-testid="benefit-share-whatsapp" style={{ borderColor: "var(--wofy-border)" }}>
          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
        </a>
        <a href={emailHref} onClick={() => logShare("email")} className={btn} data-testid="benefit-share-email" style={{ borderColor: "var(--wofy-border)" }}>
          <Mail className="w-3.5 h-3.5" /> Email
        </a>
        <button
          onClick={() => copy(url, "link")}
          className={btn}
          data-testid="benefit-share-copy-link"
          style={{
            borderColor: copied === "link" ? article.color : "var(--wofy-border)",
            color: copied === "link" ? article.color : "inherit",
          }}
        >
          {copied === "link" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied === "link" ? "Link copied" : "Copy link"}
        </button>
        <button
          onClick={() => copy(caption, "caption")}
          className={btn}
          data-testid="benefit-share-copy-caption"
          style={{
            borderColor: copied === "caption" ? article.color : "var(--wofy-border)",
            color: copied === "caption" ? article.color : "inherit",
          }}
        >
          {copied === "caption" ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          {copied === "caption" ? "Caption copied" : "Copy caption (Instagram-ready)"}
        </button>
      </div>

      {/* Read caption inline for reference */}
      <details className="mt-4 rounded-xl border px-4 py-3 text-sm" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }} data-testid="benefit-share-caption-details">
        <summary className="cursor-pointer text-xs font-mono uppercase tracking-widest opacity-70 flex items-center gap-1">
          <ExternalLink className="w-3 h-3" /> Preview caption
        </summary>
        <pre className="mt-3 whitespace-pre-wrap font-sans text-sm opacity-90">{caption}</pre>
      </details>
    </section>
  );
}
